import { useEffect, useState } from "react";
import { Mail, MapPin, ArrowRight, Check, Calendar } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Reveal from "@/components/Reveal";
import AIReadinessQuiz from "@/components/AIReadinessQuiz";
import { submitContact } from "@/lib/api";

const SERVICE_OPTIONS = [
  "AI Consultancy",
  "Website Design",
  "Marketing Strategy",
  "Social Media Management",
  "Content Strategy",
  "Social Media Design",
  "Marketing Automation",
  "Virtual Marketing Support",
  "General Enquiry",
];

const BUDGET_OPTIONS = [
  "Under £500/month",
  "£500 to £1,000/month",
  "£1,000 to £2,500/month",
  "£2,500+/month",
  "One-off project",
  "Not sure yet",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", budget: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Contact | Track Digital";
  }, []);

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service || !form.message) {
      toast.error("Please complete the required fields");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success("Thanks. We will be in touch shortly.");
      setForm({ name: "", email: "", company: "", service: "", budget: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="pt-36 md:pt-44 pb-12">
        <div className="td-container">
          <Reveal>
            <span className="td-label block mb-6">Contact</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] max-w-3xl">
              Tell us what you need.
            </h1>
            <p className="td-measure text-lg md:text-xl text-[#4a4a4a] leading-relaxed mt-8">
              Whether it&apos;s one clear project or an ongoing partnership, a short discovery call is
              the simplest way to begin. We&apos;ll talk through where you are, where you&apos;d like to get
              to and the best way to move forward.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20">
        <div className="td-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <div className="bg-white border border-[#e5e5e5] rounded-3xl p-8 md:p-10">
              {done ? (
                <div data-testid="contact-success" className="py-10 text-center">
                  <div className="w-14 h-14 rounded-full bg-[#7C6CF0] text-white flex items-center justify-center mx-auto mb-6">
                    <Check size={28} />
                  </div>
                  <h2 className="text-2xl font-semibold mb-3">Thank you.</h2>
                  <p className="text-[#6b6b6b] max-w-sm mx-auto">
                    Your enquiry is in. We will be in touch shortly to arrange a discovery call.
                  </p>
                  <button onClick={() => setDone(false)} className="td-btn td-btn-outline mt-8">
                    Send another
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="td-label block mb-2 text-[#6b6b6b]">Name *</label>
                      <input
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        data-testid="contact-name"
                        className="w-full rounded-xl border border-[#e5e5e5] px-4 py-3 outline-none focus:border-[#7C6CF0] transition-colors"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="td-label block mb-2 text-[#6b6b6b]">Email *</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        data-testid="contact-email"
                        className="w-full rounded-xl border border-[#e5e5e5] px-4 py-3 outline-none focus:border-[#7C6CF0] transition-colors"
                        placeholder="you@company.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="td-label block mb-2 text-[#6b6b6b]">Company</label>
                    <input
                      value={form.company}
                      onChange={(e) => update("company", e.target.value)}
                      data-testid="contact-company"
                      className="w-full rounded-xl border border-[#e5e5e5] px-4 py-3 outline-none focus:border-[#7C6CF0] transition-colors"
                      placeholder="Your business"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="td-label block mb-2 text-[#6b6b6b]">Service *</label>
                      <Select value={form.service} onValueChange={(v) => update("service", v)}>
                        <SelectTrigger data-testid="contact-service" className="w-full rounded-xl border-[#e5e5e5] h-[50px] px-4">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICE_OPTIONS.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="td-label block mb-2 text-[#6b6b6b]">Budget</label>
                      <Select value={form.budget} onValueChange={(v) => update("budget", v)}>
                        <SelectTrigger data-testid="contact-budget" className="w-full rounded-xl border-[#e5e5e5] h-[50px] px-4">
                          <SelectValue placeholder="Select a range" />
                        </SelectTrigger>
                        <SelectContent>
                          {BUDGET_OPTIONS.map((b) => (
                            <SelectItem key={b} value={b}>{b}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="td-label block mb-2 text-[#6b6b6b]">Message *</label>
                    <textarea
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      data-testid="contact-message"
                      rows={5}
                      className="w-full rounded-xl border border-[#e5e5e5] px-4 py-3 outline-none focus:border-[#7C6CF0] transition-colors resize-none"
                      placeholder="Tell us a little about what you need"
                    />
                  </div>

                  <button type="submit" disabled={submitting} data-testid="contact-submit" className="td-btn td-btn-primary w-full disabled:opacity-60">
                    {submitting ? "Sending..." : "Send enquiry"} <ArrowRight size={18} />
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          {/* Sidebar */}
          <Reveal delay={120} className="lg:col-span-5">
            <div className="rounded-3xl p-8 md:p-10 mb-6" style={{ background: "#ECE9FF" }}>
              <span className="td-label block mb-4">Book a Discovery Call</span>
              <h2 className="font-head text-2xl font-semibold mb-4 text-black">A calm, no-pressure conversation.</h2>
              <p className="text-[#4a4a55] leading-relaxed mb-6">
                Prefer to talk it through? Book a short discovery call and we&apos;ll help you find the clearest
                next step.
              </p>
              <button
                onClick={() => toast.info("Calendar booking coming soon. Please use the form for now.")}
                data-testid="book-call-button"
                className="td-btn td-btn-primary w-full"
              >
                <Calendar size={18} /> Book a call
              </button>
            </div>

            <div className="space-y-5 px-2">
              <div className="flex items-center gap-3">
                <Mail size={20} className="text-[#7C6CF0]" />
                <a href="mailto:hello@track-digital.com" className="text-[#2b2b2b] hover:text-[#7C6CF0] transition-colors">
                  hello@track-digital.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <MapPin size={20} className="text-[#7C6CF0]" />
                <span className="text-[#2b2b2b]">UK based</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* AI Readiness Quiz */}
      <section className="py-20 md:py-28 bg-[#fafafa]" data-testid="quiz-section">
        <div className="td-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-28">
            <span className="td-label block mb-6">Interactive Tool</span>
            <h2 className="text-3xl md:text-4xl font-semibold leading-tight mb-6">
              How AI ready is your business?
            </h2>
            <p className="text-lg text-[#4a4a4a] leading-relaxed td-measure">
              Answer five short questions to see where you stand and get a practical starting point. It takes
              about a minute.
            </p>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal delay={120}>
              <AIReadinessQuiz />
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
